import { useState, useCallback, useRef } from 'react';

import type { BadStatusError } from '@confluence/network';
import { cfetch } from '@confluence/network';
import { throwErrorWithResponse } from '@confluence/graphql/entry-points/throwErrorWithResponse';

type MutationResult<TData> = {
	data: TData | undefined;
	loading: boolean;
	error: Error | undefined;
};

type UseExperimentalMutationParams<TData, TVariables, TResponse> = {
	url: string | ((variables: TVariables) => string);
	method: string;
	headers?: HeadersInit;
	body?: string | ((variables: TVariables) => string);
	parseResult: (response: TResponse, variables: TVariables) => TData | Promise<TData>;
	onCompleted?: (data: TData) => void;
	onError?: (error: Error | BadStatusError) => void;
	maxRetries?: number;
};

type MutationFunctionOptions<TData, TVariables> = {
	variables: TVariables;
	update?: (cache: null, result: { data?: TData | null }) => void;
};

export function useExperimentalMutation<TData, TVariables, TResponse>({
	url,
	method,
	headers,
	body,
	parseResult,
	onCompleted,
	onError,
	maxRetries = 0,
}: UseExperimentalMutationParams<TData, TVariables, TResponse>): [
	(options: MutationFunctionOptions<TData, TVariables>) => Promise<MutationResult<TData>>,
	MutationResult<TData>,
] {
	const latestUrl = useRef(url);
	latestUrl.current = url;

	const latestHeaders = useRef(headers);
	latestHeaders.current = headers;

	const latestBody = useRef(body);
	latestBody.current = body;

	const latestParseResult = useRef(parseResult);
	latestParseResult.current = parseResult;

	const fetchData = useCallback(
		async (variables: TVariables, count = 0): Promise<TData> => {
			try {
				const response = await cfetch(
					typeof latestUrl.current === 'function'
						? latestUrl.current(variables)
						: latestUrl.current,
					{
						method,
						body:
							typeof latestBody.current === 'function'
								? latestBody.current(variables)
								: latestBody.current || JSON.stringify(variables),
						headers: latestHeaders.current,
					},
				);

				const json =
					response.status === 204
						? {}
						: await response.json().catch((error) => {
								throw throwErrorWithResponse(error, response);
							});

				return latestParseResult.current(json, variables);
			} catch (error) {
				if (count < maxRetries && error?.response?.status === 409) {
					return fetchData(variables, ++count);
				}
				throw error;
			}
		},
		[method, maxRetries],
	);

	return useExperimentalMutationBase<TData, TVariables>({
		fetchData,
		onCompleted,
		onError,
	});
}

type UseExperimentalMutationBaseParams<TData, TVariables> = {
	fetchData: (variables: TVariables) => TData | Promise<TData>;
	onCompleted?: (data: TData) => void;
	onError?: (error: Error | BadStatusError) => void;
};

export function useExperimentalMutationBase<TData, TVariables>({
	fetchData,
	onCompleted,
	onError,
}: UseExperimentalMutationBaseParams<TData, TVariables>): [
	(options: MutationFunctionOptions<TData, TVariables>) => Promise<MutationResult<TData>>,
	MutationResult<TData>,
] {
	const [result, setResult] = useState<MutationResult<TData>>({
		data: undefined,
		loading: false,
		error: undefined,
	});

	const latestFetchData = useRef(fetchData);
	latestFetchData.current = fetchData;

	const latestOnCompleted = useRef(onCompleted);
	latestOnCompleted.current = onCompleted;

	const latestOnError = useRef(onError);
	latestOnError.current = onError;

	const mutate = useCallback(
		async ({ variables, update }: MutationFunctionOptions<TData, TVariables>) => {
			setResult({ data: undefined, loading: true, error: undefined });

			try {
				const data = await latestFetchData.current(variables);
				update?.(null, { data });
				const newResult = { data, loading: false, error: undefined };
				setResult(newResult);
				latestOnCompleted.current?.(data);
				return newResult;
			} catch (error) {
				let finalError = error;
				try {
					await latestOnError.current?.(error);
				} catch (processedError) {
					finalError = processedError;
				}
				const newResult = { data: undefined, loading: false, error: finalError };
				setResult(newResult);
				return newResult;
			}
		},
		[],
	);

	return [mutate, result];
}
